/**
 * ComposePostModal — full-screen dialog for writing a short blog-style
 * reflection that goes directly to the Weekly feed.
 *
 * The Oracle route yields structured correlations (question → response). This
 * flow is the opposite: the user has already had the insight; they just want
 * to share it. Title + body only, no linked traditions (for now).
 *
 * On successful submit → navigates to `/weekly#item-<new_id>` so the author
 * lands on their own post and can see how it looks in the feed.
 */
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, PenLine, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";

const TITLE_MAX = 140;
const BODY_MAX = 8000;

export default function ComposePostModal({ open, onClose, prefill = null }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const titleRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      setTitle(prefill?.title || "");
      setBody(prefill?.body || "");
      // Autofocus the title after the portal mounts.
      setTimeout(() => { titleRef.current?.focus(); }, 60);
    }
  }, [open, prefill]);

  // Close on Esc.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const submit = async () => {
    const t = title.trim();
    const b = body.trim();
    if (!t || !b) {
      toast.error("Title and body are both required.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/posts", { title: t, body: b });
      toast.success("Posted to Weekly.");
      onClose?.();
      // Jump the author to their own post so they can see it live and share it.
      navigate(`/weekly#item-${data.saved.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not post. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;
  const canSubmit = title.trim().length > 0 && body.trim().length > 0 && !submitting;

  return createPortal(
    <div
      data-testid="compose-post-modal"
      className="fixed inset-0 z-[110] bg-black/60 backdrop-blur-sm flex items-start sm:items-center justify-center p-0 sm:p-6"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        className="w-full max-w-2xl bg-background sm:hairline sm:rounded-none max-h-[100vh] sm:max-h-[90vh] flex flex-col"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-[hsl(var(--border))]">
          <div>
            <div className="label mb-1 text-[hsl(var(--accent))]">New Post</div>
            <h2 className="serif text-2xl text-foreground leading-tight">
              Share a thought, revelation, or observation.
            </h2>
            <p className="text-[12px] text-muted-foreground mt-1 leading-relaxed">
              Goes straight to the Weekly feed. No traditions or Oracle framing needed.
            </p>
          </div>
          <button
            type="button"
            data-testid="compose-close"
            onClick={onClose}
            className="p-2 text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          <div>
            <label className="label mb-2 block text-muted-foreground">Title</label>
            <input
              ref={titleRef}
              data-testid="compose-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value.slice(0, TITLE_MAX))}
              placeholder="A one-line hook…"
              className="input-line w-full serif text-2xl leading-tight"
              maxLength={TITLE_MAX}
            />
            <div className="mt-1 text-right mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              {title.length}/{TITLE_MAX}
            </div>
          </div>
          <div>
            <label className="label mb-2 block text-muted-foreground">Body</label>
            <textarea
              data-testid="compose-body"
              value={body}
              onChange={(e) => setBody(e.target.value.slice(0, BODY_MAX))}
              rows={12}
              placeholder="Say it in your own voice. What did you notice? What was the resonance? Two lines or twenty — whatever the thought needs."
              className="input-line w-full text-[15px] leading-relaxed resize-none"
              maxLength={BODY_MAX}
            />
            <div className="mt-1 flex items-center justify-between mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              <span className="italic normal-case tracking-normal font-serif text-[11px] text-muted-foreground/80">
                Tip: plain text works. Line breaks preserved.
              </span>
              <span>{body.length}/{BODY_MAX}</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-5 border-t border-[hsl(var(--border))]">
          <button
            type="button"
            data-testid="compose-cancel"
            onClick={onClose}
            disabled={submitting}
            className="mono text-[10px] uppercase tracking-[0.2em] px-4 py-2 text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="compose-submit"
            onClick={submit}
            disabled={!canSubmit}
            className="mono text-[10px] uppercase tracking-[0.2em] px-4 py-2 border-2 border-[hsl(var(--primary))] text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary)/0.08)] transition-colors disabled:opacity-40 inline-flex items-center gap-2"
          >
            {submitting ? <Loader2 size={13} className="animate-spin" /> : <PenLine size={13} />}
            {submitting ? "Posting…" : "Post to Weekly"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
